import { Tooltip } from './Tooltip';
import { RANKING_TERMS, type TermKey } from '../lib/rankingTerms';

const RECEIPT_CAP = 8;

/** A term's explanation without its label: definition, how it is computed, the caveat, an optional
 *  data-carrying `extra` line and the receipts. For tooltips whose header is not the bare label. */
export function termBody(term: TermKey, receipts?: string[], extra?: React.ReactNode): React.ReactNode {
  const t = RANKING_TERMS[term];
  const shown = receipts?.slice(0, RECEIPT_CAP) ?? [];
  const more = (receipts?.length ?? 0) - shown.length;
  return (
    <>
      <div>{t.definition}</div>
      <div className="text-on-surface-variant">{t.computed}</div>
      {t.caveat && <div className="text-on-surface-variant italic">{t.caveat}</div>}
      {extra != null && extra !== false && <div className="text-on-surface-variant">{extra}</div>}
      {shown.length > 0 && (
        <div className="text-on-surface-variant">
          From: {shown.join(', ')}{more > 0 ? ` +${more} more` : ''}
        </div>
      )}
    </>
  );
}

export function termTooltip(term: TermKey, receipts?: string[], extra?: React.ReactNode): React.ReactNode {
  return (
    <div className="flex flex-col gap-1">
      <div className="font-semibold">{RANKING_TERMS[term].label}</div>
      {termBody(term, receipts, extra)}
    </div>
  );
}

/** The term's chip. `tooltip` replaces the term's standard tooltip (a chip whose tooltip leads with data,
 *  such as a facet closure); `receipts` and `extra` feed the standard one. */
export function ScoreTerm({ term, receipts, extra, tooltip, className = '' }: {
  term: TermKey; receipts?: string[]; extra?: React.ReactNode; tooltip?: React.ReactNode; className?: string;
}) {
  return (
    <Tooltip content={tooltip ?? termTooltip(term, receipts, extra)} popupWidth={tooltip ? 'max-w-[320px]' : 'max-w-[300px]'}>
      <span tabIndex={0}
        className={`text-xxs px-1 py-[1px] rounded-[3px] bg-[var(--surface-container-high)] text-on-surface-variant cursor-help ${className}`}>
        {RANKING_TERMS[term].label}
      </span>
    </Tooltip>
  );
}
